import { JSX as IonicJSX } from '@ionic/core';
import { IonToast } from '@ionic/react';

import { mergeClassNames } from '@/utils/mergeClassNames';

import WarningSign from '@/assets/icons/warningSign.svg';

import styles from './Toast.module.css';

type IonicToastProps = IonicJSX.IonToast;

interface ToastProps extends IonicToastProps {
  clearError?: () => void;
  type: 'info' | 'warning';
}

/**
 * Toast component that displays a message to the user.
 *
 * @param {Object} props - The properties object.
 * @param {function} props.clearError - Function to clear the error when the toast is dismissed.
 * @param {string} props.type - The type of the toast, can be 'info' or 'warning'.
 * @param {boolean} props.isOpen - Boolean indicating whether the toast is open.
 * @param {string} [props.message] - The message to display in the toast.
 * @param {string} [props.position='bottom'] - The position of the toast on the screen.
 * @param {string} [props.header] - The header text of the toast.
 * @param {string} [props.className] - Additional class names for the toast.
 * @param {React.ReactNode} [props.icon] - Icon to display in the toast.
 *
 * @returns {JSX.Element} The rendered Toast component.
 */
export const Toast = ({ clearError, type, isOpen, duration = 5000, ...props }: ToastProps): JSX.Element => {
  return (
    <IonToast
      className={mergeClassNames(styles.toast, type === 'info' && styles.info, type === 'warning' && styles.warning)}
      duration={duration}
      header={props.header}
      icon={type === 'warning' ? WarningSign : undefined}
      isOpen={isOpen}
      message={props.message}
      onDidDismiss={clearError ? () => clearError() : undefined}
      position={props.position || 'bottom'}
    />
  );
};
